import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { useJDs, useMoveJD, useReanalyse, type JDSummary } from "@/api/jd-queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, ErrorNote, Panel } from "@/components/ui/panel";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/**
 * The upload page's second and third steps — DESIGN §20.5, §20.11. While the one model call reads the
 * document, a card saying so; then one card per position it found, each kept or discarded, and the way
 * into reviewing the kept ones. Discarding is a status (`discarded`, with the reason the document gave),
 * so a position switched off here can be switched back on.
 */

export function UploadSteps({ at }: { at: 1 | 2 | 3 }) {
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Upload steps">
      {(["Add the JD", "AI reads it", "Choose positions"] as const).map((name, i) => {
        const n = i + 1;
        const done = n < at;
        return (
          <li key={name} className="flex items-center gap-2">
            {i > 0 && <span className="bg-bank-border-strong h-px w-10" aria-hidden="true" />}
            <span
              aria-current={n === at ? "step" : undefined}
              className={cn(
                "flex items-center gap-2 text-[13px]",
                n === at ? "font-semibold" : "text-bank-muted",
              )}
            >
              <span
                className={cn(
                  "grid size-[22px] place-items-center rounded-full border-[1.5px] font-mono text-[11.5px]",
                  done && "border-bank-good bg-bank-good-wash text-bank-good-ink",
                  n === at && "border-bank-accent bg-bank-accent-wash text-bank-accent-ink",
                  n > at && "border-bank-border-strong bg-bank-surface",
                )}
              >
                {done ? <Check className="size-3" aria-hidden="true" /> : n}
              </span>
              {name}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function PositionCard({ jd }: { jd: JDSummary }) {
  const move = useMoveJD(jd.id);
  const discarded = jd.status === "discarded";
  const share = jd.skills_total > 0 ? jd.skills_covered / jd.skills_total : 0;
  return (
    <article
      className={cn(
        "bg-bank-surface flex flex-col gap-2.5 rounded-lg border p-3.5",
        discarded
          ? "border-bank-border-strong bg-bank-surface-2 border-dashed"
          : jd.stacks.length
            ? "border-bank-border"
            : "border-bank-attention",
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">
            Position {jd.position_index + 1}
          </p>
          <p className={cn("text-[14.5px] font-semibold", discarded && "text-bank-muted line-through")}>
            {jd.title}
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-[12.5px] font-medium">
          Keep
          <Switch
            checked={!discarded}
            label={discarded ? `Keep ${jd.title}` : `Discard ${jd.title}`}
            disabled={move.isPending || (jd.status !== "in_review" && !discarded)}
            onChange={(keep) => move.mutate({ status: keep ? "in_review" : "discarded" })}
          />
        </span>
      </div>
      {discarded ? (
        <p className="text-bank-attention-ink text-[12.5px]">
          {jd.discard_reason
            ? `Discarded by the AI — the document said: “${jd.discard_reason}”.`
            : "Discarded."}{" "}
          Switch it back on to keep it.
        </p>
      ) : (
        <>
          <p className="flex flex-wrap items-center gap-1.5">
            {jd.stacks.map((s) => (
              <span
                key={s}
                className="bg-bank-inert-wash text-bank-inert-ink rounded px-1.5 py-0.5 font-mono text-[11.5px]"
              >
                {s}
              </span>
            ))}
            <span className="text-bank-muted text-xs">
              {jd.skills_total} skills{jd.stacks.length ? "" : " · no bank stack yet"}
            </span>
          </p>
          {jd.stacks.length ? (
            <div>
              <div className="bg-bank-surface-3 h-1.5 overflow-hidden rounded-full">
                <div
                  className="bg-bank-accent h-full rounded-full"
                  style={{ width: `${Math.round(share * 100)}%` }}
                />
              </div>
              <p className="text-bank-muted mt-1 font-mono text-[11px]">
                {jd.skills_covered} / {jd.skills_total} skills the bank can ask about
              </p>
            </div>
          ) : (
            <Callout>
              You can review it now. Publishing needs one technical skill mapped to a stack with questions.
            </Callout>
          )}
        </>
      )}
      <ErrorNote error={move.error} />
    </article>
  );
}

export function Positions({ sourceId, onAnother }: { sourceId: number; onAnother: () => void }) {
  const jds = useJDs();
  const reanalyse = useReanalyse();
  const source = jds.data?.sources.find((s) => s.id === sourceId);
  const positions = (jds.data?.items ?? [])
    .filter((jd) => jd.source_id === sourceId)
    .sort((a, b) => a.position_index - b.position_index);
  const kept = positions.filter((jd) => jd.status !== "discarded");
  const reading = source !== undefined && source.status !== "failed";

  if (source?.status === "failed") {
    return (
      <Panel className="space-y-3 p-4">
        <Callout tone="bad">The analysis failed: {source.error}</Callout>
        <div className="flex gap-2">
          <Button disabled={reanalyse.isPending} onClick={() => reanalyse.mutate(sourceId)}>
            Try again
          </Button>
          <Button variant="ghost" onClick={onAnother}>
            Upload something else
          </Button>
        </div>
        <ErrorNote error={reanalyse.error} />
      </Panel>
    );
  }
  if (reading || (positions.length === 0 && jds.isFetching)) return <Reading />;
  if (positions.length === 0) {
    return (
      <Panel className="space-y-3 p-4">
        <Callout>The AI found no position in it. If it is a scanned PDF, paste the text instead.</Callout>
        <Button variant="ghost" onClick={onAnother}>
          Upload something else
        </Button>
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-[15px] font-semibold">
          {positions.length === 1
            ? "1 position found"
            : `${positions.length} positions found in one document`}
        </h2>
        <p className="text-bank-muted text-[13px]">
          Keep the ones the client needs. Each kept position becomes its own job description, with its own
          skills and status.
        </p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3">
        {positions.map((jd) => (
          <PositionCard key={jd.id} jd={jd} />
        ))}
      </div>
      <Panel className="sticky bottom-3 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 shadow-lg">
        <span className="font-semibold">{kept.length} kept</span>
        <span className="text-bank-muted">· {positions.length - kept.length} discarded</span>
        <span className="text-bank-muted text-[12.5px]">
          No questions are needed to publish — you can add them later.
        </span>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onAnother}>
            Upload another
          </Button>
          {kept[0] && (
            <Link
              to={`/jds/${kept[0].id}`}
              className="bg-bank-accent-strong text-bank-on-accent hover:bg-bank-accent-hover inline-flex h-10 items-center gap-1.5 rounded-md px-4 text-sm font-medium"
            >
              Start reviewing {kept.length === 1 ? "the position" : `the ${kept.length} positions`}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      </Panel>
    </div>
  );
}

export function Reading() {
  return (
    <Panel className="flex max-w-xl flex-col gap-3 p-4">
      <div className="flex items-center gap-2.5">
        <span className="bg-bank-pipe-wash text-bank-pipe grid size-9 place-items-center rounded-full">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        </span>
        <div>
          <p className="font-semibold">Reading the job description…</p>
          <Badge value="analysing" />
        </div>
      </div>
      <p className="text-[13px]">
        One AI call finds every position in it and the skills each asks for, then each skill is matched to the
        bank's stacks.
      </p>
      <p className="text-bank-muted border-bank-border border-t pt-2.5 text-[12.5px]">
        Usually under a minute. If you leave, it carries on — the upload shows in the list's Processing strip
        until its positions appear.
      </p>
    </Panel>
  );
}
